import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { delay, map, switchMap, take, tap } from 'rxjs/operators';

/**
 * Create a form-control validator, which compares the control's
 * value with the list provided with `nameIdSource$` and sets
 * the error to `errorMsg`.
 *
 * `currentIdGetter` is a callback, which provides the ID
 * of the value to check.
 * This ID will be checked against the IDs in the source list.
 * If it matches an ID from the source list, the matched value
 * will not be compared to the current control's value.
 *
 * Note: if the source list does not contain IDs and `currentIdGetter`
 * returns `undefined`, nothing will be compared.
 *
 * In that case it might be more logical to have a symbol (`Symbol()`)
 * returned by `currentIdGetter`.
 *
 * If `errorMessage` contains braces with arbitrary content, e.g. `{theName}`,
 * it will be replaced with with the value that failed the check.
 *
 * Structure of error object: `{ nameTaken: 'The name "theName" is already taken.' }`
 *
 * @param nameIdSource$
 * @param currentIdGetter
 * @param errorMsg
 * @returns
 */
export function createNameTakenValidator(
  nameIdSource$: Observable<{ _id?: string; name: string }[]>,
  // eslint-disable-next-line @typescript-eslint/no-restricted-types -- implementing angular interface
  currentIdGetter: () => (string | undefined)[] | string | undefined | Symbol,
  errorMsg: string
): AsyncValidatorFn {
  let oldValue: string | undefined;
  let oldErrors: ValidationErrors | null;

  return (control: AbstractControl): Observable<ValidationErrors | null> => of(control.value).pipe(
    delay(100), // debounce
    map(value => value === oldValue ? null : value), // (*)distinctUntilchanged-remake up to...
    tap(_ => oldValue = control.value), // (store the new value for the next distinction)
    switchMap(nameToCheck => {
      if (nameToCheck === null) {
        // Name was not changed. Return the same state-result as before.
        return of(oldErrors);
      }

      const nameToCheckLower = nameToCheck.toLowerCase();
      const currentId = currentIdGetter();
      let currentIds: any = '';
      if (Array.isArray(currentId)) {
        currentIds = currentId.join(' ');
      } else {
        currentIds = currentId;
      }

      // Now we have a distinct name that we can check for "taken-ness"
      return nameIdSource$.pipe(
        take(1),
        map(list => list
          // Ignore the existing element that represents
          // the name to check - if it exists in the list.
          .filter(entry => !currentIds.includes(entry._id))
          .every(
            entry => entry.name.toLowerCase() !== nameToCheckLower
          ) ? null : { nameTaken: errorMsg.replace(/{[^{}]*}/gm, control.value) }),
        tap(result => oldErrors = result)
      );
    })
  );
}

export function createNameTakenValidatorWithList(
  nameIdSource: { _id?: string; name: string }[],
  // eslint-disable-next-line @typescript-eslint/no-restricted-types -- implementing angular interface
  currentIdGetter: () => (string | undefined)[] | string | undefined | Symbol,
  errorMsg: string
): AsyncValidatorFn {
  let oldValue: string | undefined;
  let oldErrors: ValidationErrors | null;

  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    let nameToCheck = control.value;
    if (nameToCheck === null
      || nameToCheck === oldValue
    ) {
      // Name was not changed. Return the same state-result as before.
      return of(oldErrors);
    }

    oldValue = nameToCheck;

    const nameToCheckLower = nameToCheck.toLowerCase();
    const currentId = currentIdGetter();
    let currentIds: any = '';
    if (Array.isArray(currentId)) {
      currentIds = currentId.join(' ');
    } else {
      currentIds = currentId;
    }

    return of(nameIdSource).pipe(
      delay(100),
      map(nameIdSource => {
        const isTaken = nameIdSource
          .filter(entry => !currentIds.includes(entry._id))
          .some(
            item => item.name.toLowerCase() === nameToCheckLower
          );

        const result = isTaken ? { nameTaken: errorMsg.replace(/{[^{}]*}/gm, control.value) } : null;
        oldErrors = result;
        return result;
      })
    );
  };
}
